/**
 * The Cockpit's perimeter in the top bar, and its states in the page strip.
 *
 * **The run is in the top bar** (brief part 1 §6, designer item 2.7: «il run va nella barra in
 * alto»). `RunScope` portals into the chrome's slot (`../../chrome/top-bar-slot.tsx`): the market,
 * the run — whose chip opens the picker in its popover — a stale-stock flag when the run was
 * computed on a quarantined partition, and, when the price figures read another run (F16), which
 * one. Nothing of it is drawn in the page body any more.
 *
 * **The page's states are one strip** (brief §4, ADR 9033). `CockpitStatus` registers what the
 * reads answered — a failure, a refusal, a read not published, a run unknown — into the page's
 * `PageStatusRegion`, which shows the worst and «e altri N». Before, the Cockpit said these as a
 * chip each in "Data behind this page" and a notice per panel.
 *
 * DEC-87 moved the page's operational sections to their apps' pages; see `OverviewScreen.tsx`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CycleRunSummary } from '@/api/generated';
import { NEWEST_RUN_OPTION, runPickerWords, runSlotFallback, runWords, runWordsFallback, usePageStatusEntry, type PageStatusEntry } from '@/planning';
import { codeWords, ENTRY_POINT_LABEL_KEY } from '@/planning/words';
import { formatDateTime } from '@/lib/format';
import { Button, Popover, Select, StatusIndicator, FOCUS_RING } from '@/primitives';

import { TopBarPortal } from '../../chrome/top-bar-slot';
import { coverageKind, type CoverageEntry } from './cockpit';

/**
 * A date rendered for a planner rather than for a machine: `toLocaleDateString` in the active
 * language. An unparseable value renders as itself rather than as `Invalid Date`.
 */
function onDate(value: string, language: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString(language);
}

/** A label and its value, as the designer's top-bar chips draw them (brief §8). */
/** An orchestrator tick as a date and a time in UTC (audit F1); an unreadable one verbatim. */
function tickMoment(value: string, language: string): string {
  return Number.isNaN(new Date(value).getTime()) ? value : formatDateTime(value, language, 'UTC');
}

function Chip({ labelKey, children, title }: {
  readonly labelKey: string;
  readonly children: React.ReactNode;
  readonly title?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="text-caption flex shrink-0 items-center gap-2 whitespace-nowrap" title={title}>
      <span className="text-ink-label">{t(labelKey)}</span>
      <strong className="text-ink-primary font-semibold">{children}</strong>
    </span>
  );
}

export interface RunScopeProps {
  /** The run the figures are about; `undefined` while the directory has not named it. */
  readonly run: CycleRunSummary | undefined;
  /** The directory the popover offers — one page, newest first. */
  readonly runs: readonly CycleRunSummary[];
  readonly current: string;
  readonly onChoose: (runId: string) => void;
  /** The run the price figures read, when it is not `current` (F16). */
  readonly priceRun: { readonly runId: string; readonly run: CycleRunSummary | undefined } | null;
}

/**
 * The market and the run in the top bar, "Change run" in the run chip's popover.
 *
 * **Staleness is stated in the bar, not only in the picker.** `list_runs` returns runs computed on
 * a quarantined stock partition rather than filtering them, and the Cockpit resolves the NEWEST
 * run, which may be one — so the flag is on every view of the page, in words (§12.6).
 *
 * The run is still RESOLVED, NAMED and WRITTEN to the URL (ADR 9003, `scope.ts`); the chip only
 * moved. A planner's `?runId=` always wins over the directory.
 */
export function RunScope({ run, runs, current, onChoose, priceRun }: RunScopeProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [chosen, setChosen] = useState(current);

  // «Il più recente» first (DEC-99): it forgets the planner's choice, so the Cockpit follows the newest run.
  const options = [{ value: NEWEST_RUN_OPTION, labelKey: 'runDefault.newestOption' } as const, ...runs.map((row) => ({
    value: row.run_id,
    // Name and the minute it ran, a stale stock position said in the same sentence (audit 2, B).
    label: runPickerWords(row, t, i18n.language),
  }))];
  // A run the directory does not name is «indicata», never its id (audit A1).
  const runLabel = run === undefined ? runSlotFallback(t, i18n.language) : t('overview.run.chipValue', { country: run.country_key, period: run.period_key });
  const stale = run !== undefined && !run.stock_partition_is_fresh;
  // Both runs named in words — market, period, channel and the day (audit A1) — never by their ids.
  const pricedWords = priceRun === null ? '' : priceRun.run === undefined ? runWordsFallback(t, i18n.language) : runWords(priceRun.run, t, i18n.language);
  const priceRunMovedValues = { run: run === undefined ? runWordsFallback(t, i18n.language) : runWords(run, t, i18n.language), pricedRun: pricedWords };

  return (
    <div className="flex min-w-0 items-center gap-4 overflow-x-auto" data-run-chip="">
      {run !== undefined && <Chip labelKey="chooseRun.market">{run.country_key}</Chip>}
      <Popover
        triggerLabelKey="overview.run.change"
        align="start"
        trigger={(
          // The chip the planner chooses with, so it carries the 2px rule the brief gives the
          // Run selector (§4, «è la scelta da fare»).
          <button
            type="button"
            aria-label={t('overview.run.chipLabel', { run: runLabel })}
            className={`border-ink-primary text-caption flex h-[32px] shrink-0 items-center gap-2 whitespace-nowrap border-2 px-2 ${FOCUS_RING}`}
          >
            <span className="text-ink-label">{t('chooseRun.run')}</span>
            <strong className="font-numeric text-ink-primary font-semibold">{runLabel}</strong>
            <span aria-hidden className="text-ink-label">▾</span>
          </button>
        )}
      >
        <div className="flex max-w-[24rem] flex-col gap-3">
          {run !== undefined && (
            <p className="text-body text-ink-primary">
              {t('overview.run.perimeter', {
                country: run.country_key,
                period: run.period_key,
                entryPoint: codeWords(t, ENTRY_POINT_LABEL_KEY, run.entry_point_id),
                generated: onDate(run.generated_at, i18n.language),
              })}
            </p>
          )}
          {/* The orchestrator tick that produced the run (bata-platform ADR 6034) — data, named when
              the platform recorded one; a backfill or a manual launch has none. */}
          {run !== undefined && typeof run.cycle_tick === 'string' && run.cycle_tick !== '' && (
            <p className="text-caption text-ink-secondary">{t('overview.run.tick', { tick: tickMoment(run.cycle_tick, i18n.language) })}</p>
          )}
          {run !== undefined && (run.stock_partition_is_fresh
            ? <StatusIndicator level="good" labelKey="overview.run.freshStock" />
            : <StatusIndicator level="warning" labelKey="overview.run.staleStock" />)}
          {runs.length > 0 && (
            <>
              <Select
                labelKey="overview.run.pickerLabel"
                helpKey="overview.run.pickerHelp"
                options={options}
                value={chosen}
                onValueChange={setChosen}
              />
              <Button variant="secondary" onClick={() => onChoose(chosen)} disabled={chosen === current}>
                {t('overview.run.show')}
              </Button>
            </>
          )}
        </div>
      </Popover>
      {stale && (
        <span className="text-status-critical-strong text-caption flex shrink-0 items-center gap-1 whitespace-nowrap font-semibold" data-stale-stock="">
          <span aria-hidden className="font-numeric">!</span>
          <span aria-hidden>{t('overview.run.staleShort')}</span>
          <span className="sr-only">{t('overview.run.staleStock')}</span>
        </span>
      )}
      {priceRun !== null && (
        // F16: the price figures read another run than the cycle run on screen — both named.
        <span className="flex shrink-0 items-center" data-price-run="">
          <Chip
            labelKey="overview.run.priceChip"
            title={t('overview.markdown.priceRunMoved', priceRunMovedValues)}
          >
            <span aria-hidden className="font-numeric">
              {t('overview.run.priceChipValue', { run: pricedWords })}
            </span>
            <span className="sr-only">
              {t('overview.markdown.priceRunMoved', priceRunMovedValues)}
            </span>
          </Chip>
        </span>
      )}
    </div>
  );
}

/** The run scope, in the top bar — nothing where there is no bar. */
export function RunScopeInTopBar(props: RunScopeProps): React.JSX.Element {
  return <TopBarPortal><RunScope {...props} /></TopBarPortal>;
}

// ── the page strip ───────────────────────────────────────────────────────────

/**
 * What one read's answer says in the page strip, or `null` when it says nothing there.
 *
 * Loading and empty say nothing in the strip: loading is the figures' own «—», and an empty answer
 * is stated where its figure would stand. A run unknown or out of scope is one sentence for every
 * run-bound read (the platform does not say which, on purpose), so it registers once.
 */
export function readStatusEntry(entry: CoverageEntry, read: string): PageStatusEntry | null {
  switch (coverageKind(entry.state)) {
    case 'failed':
      return {
        kind: 'error',
        message: { key: 'overview.status.readFailed', values: { read } },
        ...(entry.state.kind === 'unavailable' ? { error: entry.state.error } : {}),
        ...(entry.retry === undefined ? {} : { retry: entry.retry }),
      };
    case 'noPermission':
      return { kind: 'no-permission', message: { key: 'overview.status.noPermission', values: { read } } };
    case 'notBuilt':
      return { kind: 'not-published', message: { key: 'overview.status.notPublished', values: { read } } };
    case 'notFound':
      return { kind: 'awaiting', message: { key: 'overview.status.runNotFound' } };
    default:
      return null;
  }
}

function ReadStatus({ entry }: { readonly entry: PageStatusEntry }): null {
  usePageStatusEntry(entry);
  return null;
}

/** The page's states, registered into its one strip. Renders nothing itself. */
export function CockpitStatus({ coverage, noRun }: {
  readonly coverage: readonly CoverageEntry[];
  /** The run directory answered empty: no cycle has recorded a run. */
  readonly noRun: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const seen = new Set<string>();
  const entries: { readonly id: string; readonly entry: PageStatusEntry }[] = [];
  if (noRun) {
    entries.push({ id: 'noRun', entry: { kind: 'awaiting', message: { key: 'overview.noRun.title' }, detail: { key: 'overview.noRun.body' } } });
  }
  for (const read of coverage) {
    const entry = readStatusEntry(read, t(read.labelKey));
    if (entry === null) continue;
    // One sentence once: the run-unknown refusal is the same statement for every run-bound read.
    const id = entry.kind === 'awaiting' ? 'runNotFound' : read.id;
    if (seen.has(id)) continue;
    seen.add(id);
    entries.push({ id, entry });
  }
  return <>{entries.map(({ id, entry }) => <ReadStatus key={id} entry={entry} />)}</>;
}
